import { InjectionToken, inject } from '@angular/core';
import { PaymentResponseStatus } from '@domain/payment/PaymentReponseStatus';
import type { Cabecera } from '@features/seguimiento/cabecera';
import { Desenlace } from './desenlace';
import { PagoDeSolicitud } from './pago-de-solicitud';

/**
 * De que tramite es el pago que la pantalla de retorno viene a contar
 * (SPEC-016, Fase 2).
 *
 * **La espera es la misma en los dos tramites** —sondear al middleware, la tabla
 * de estados, cuando parar— y por eso la pantalla se comparte. Lo que cambia son
 * tres cosas, y son justo las de este contrato:
 *
 * 1. **De donde sale el `paymentId`.** Una matricula nueva lo lleva en la
 *    solicitud; una renovacion, en la membresia que se creo al pagar, y a esa se
 *    llega por el `uuid` de la direccion.
 * 2. **Que se anota.** La matricula nueva escribe `payment.paymentStatus`. La
 *    renovacion **no escribe nada**: su cierre lo hace la Logic App de
 *    renovaciones, y anotar aqui seria competir con ella.
 * 3. **A donde se sale, y que se dice.** Al estado del tramite, o al inicio — una
 *    renovacion no tiene radicado que consultar, porque no hay nada que aprobar—
 *    y el texto tiene que decir lo mismo que el boton. **Lo encontro lab el
 *    2026-09-08**: el pago de una renovacion salio aprobado y la pantalla dijo
 *    *"entra cuando quieras a mirar el estado del tramite"* con un boton que
 *    lleva al inicio. Aquel texto lo escribio SPEC-013 para la matricula nueva, y
 *    ahi es cierto.
 */
export interface OrigenDelPago {
  /** El pago que se consulta, o `null` si no se pudo saber cual es. */
  buscar(id: string): Promise<string | null>;

  /** Lo que esta pantalla anota, si su tramite anota algo. */
  anotar(id: string, ultimo: PaymentResponseStatus): Promise<void>;

  /** A donde lleva la salida, y como se llama. */
  salida(id: string): SalidaDelRetorno;

  /**
   * El numero con el que la persona puede preguntar por su pago, **si en este
   * tramite hace falta ensenarlo aqui**.
   *
   * Una matricula nueva ya tiene el suyo —el radicado— y esta pantalla lleva a
   * el. Una renovacion **no radica nada**, asi que quien pagaba se iba sin ningun
   * numero anotado: ese era el hueco.
   */
  numeroDeSeguimiento(pago: string): SeguimientoDelPago | null;

  /**
   * Lo que dice cada desenlace en **este** tramite.
   *
   * La tabla de estados es la misma —eso lo decide `desenlace.ts`—; lo que cambia
   * es a que puede volver la persona despues, y eso lo dice el texto.
   */
  cabeceras(): Record<Desenlace, Cabecera>;

  /**
   * Que hacer cuando la espera se agota. **Tampoco es lo mismo**: una matricula
   * nueva se retoma por el enlace del correo, y una renovacion no tiene enlace.
   */
  esperaAgotada(): string;
}

export interface SalidaDelRetorno {
  enlace: string[];
  etiqueta: string;
}

export interface SeguimientoDelPago {
  etiqueta: string;
  numero: string;
}

/**
 * **Sin proveedor, el pago es el de una solicitud**, que es el tramite que ya
 * recorria esta pantalla. La ruta de retorno de la renovacion provee el suyo.
 */
export const ORIGEN_DEL_PAGO = new InjectionToken<OrigenDelPago>('OrigenDelPago', {
  providedIn: 'root',
  factory: () => inject(PagoDeSolicitud),
});
